import { useQuery } from '@apollo/client';
import { MovieCreation } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useRenameShow } from '~/api/shows';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { InlineEditTitle, Toolbar } from '~/components/toolbar';
import { useAppContext, useFolderContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { i18n } from '~/lib/i18n';
import { ShowToolbarDocument } from './ShowToolbar.generated';
import { Actions } from './actions';

export const ShowToolbar = () => {
  const { t } = useTranslation();
  const [rename] = useRenameShow();
  const { currentNetwork } = useAppContext();
  const { folderTree } = useFolderContext();
  const link = useLink();
  const navigate = useNavigate();
  const params = useParams<{ showId: string; showFolderId: string }>();
  const showFolderId = Number(params.showFolderId);
  const showId = parseInt(params.showId ?? '');

  const [searchParams] = useSearchParams();
  searchParams.delete('viewId');

  const { data, loading } = useQuery(ShowToolbarDocument, {
    variables: { networkId: currentNetwork.id, showId, showFolderId },
  });

  const show = data?.network?.show;

  if (loading || !show) return !loading ? <Navigate to={link('/not-found')} replace /> : null;

  return (
    <Toolbar
      pageTitle={show.name}
      actions={<Actions show={show} />}
      titleIcon={<MovieCreation />}
      titleEditor={
        <InlineEditTitle
          inputId={`shows-item-${show.id}-name`}
          value={show.name}
          update={(value) => rename({ variables: { showId: show.id, name: value.trim() } })}
          tooltip={t('pages.shows.detail.toolbar.edit_name_tooltip')}
          validationSchema={object({
            name: string()
              .required(i18n.t('pages.shows.detail.toolbar.validation.name_required'))
              .trim(),
          })}
        />
      }
      {...(!show.origin
        ? {
            breadcrumbsLabel: (
              <FolderBreadcrumbs
                allItems={() =>
                  navigate(
                    link({
                      pathname: `/shows/${folderTree.id}`,
                      search: searchParams.toString(),
                    }),
                  )
                }
                label={t('pages.shows.detail.toolbar.all_shows_breadcrumb')}
                changeFolder={(id) =>
                  navigate(link({ pathname: `/shows/${id}`, search: searchParams.toString() }))
                }
                folder={data.network.showFolder}
                contentPage={true}
              />
            ),
          }
        : {})}
    />
  );
};
